import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Paper,
	Typography,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Checkbox,
	ListItemText,
} from "@mui/material";
import {
	ArrowBack as ArrowBackIcon,
	ArrowForward as ArrowForwardIcon,
	Warehouse as WarehouseIcon,
	Refresh as RefreshIcon,
} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import { getFacilityMapping } from "./services/api";

function FacilitySelection({ facilityMapping, selectedFacilities, onChange, onNext, onBack, nextLoading = false }) {
	const [codeToFacilities, setCodeToFacilities] = useState({});
	const [mappingLoading, setMappingLoading] = useState(true);
	const [mappingError, setMappingError] = useState("");

	useEffect(() => {
		setMappingLoading(true);
		setMappingError("");
		getFacilityMapping()
			.then((res) => res.data)
			.then((data) => {
				if (data.success && data.code_to_facilities) {
					const ct = data.code_to_facilities;
					setCodeToFacilities(ct);
					// Only set "all selected" when there is no existing selection (e.g. first time); resume keeps restored state
					if (!selectedFacilities || selectedFacilities.length === 0) {
						const allSubFacilities = Object.values(ct).flat();
						const grouped = Object.fromEntries(Object.keys(ct).map((m) => [m, ct[m]]));
						onChange(allSubFacilities, grouped);
					}
				}
			})
			.catch((err) => {
				console.error("Error loading facility mapping:", err);
				setMappingError("Failed to load facility options. Please refresh and try again.");
			})
			.finally(() => setMappingLoading(false));
	}, []);

	const handleMainFacilityChange = (mainCode, selectedSubFacilities) => {
		const allSubFacilities = codeToFacilities[mainCode] || [];
		const otherSelected = selectedFacilities.filter((f) => !allSubFacilities.includes(f));
		const newSelection = [...otherSelected, ...selectedSubFacilities];
		const grouped = Object.keys(codeToFacilities).reduce((acc, m) => {
			acc[m] = (codeToFacilities[m] || []).filter((f) => newSelection.includes(f));
			return acc;
		}, {});
		onChange(newSelection, grouped);
	};

	const handleSelectAll = (mainCode) => {
		handleMainFacilityChange(mainCode, codeToFacilities[mainCode] || []);
	};

	const handleClearAll = (mainCode) => {
		handleMainFacilityChange(mainCode, []);
	};

	const getSelectedForMainFacility = (mainCode) => {
		const subFacilities = codeToFacilities[mainCode] || [];
		return selectedFacilities.filter((f) => subFacilities.includes(f));
	};

	const mainFacilities = Object.keys(codeToFacilities).sort();
	const totalSubFacilities = Object.values(codeToFacilities).flat().length;

	return (
		<Box>
			<Box sx={{ mb: 3 }}>
				<Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
					Select Warehouse Facilities
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Choose sub-facilities for each main facility. Use the multi-select dropdowns—all are selected by default.
				</Typography>
			</Box>

			{mappingLoading && (
				<Box display="flex" alignItems="center" justifyContent="center" py={6}>
					<CircularProgress size={32} />
				</Box>
			)}

			{mappingError && (
				<Alert severity="error" sx={{ mb: 2 }} action={
					<Button size="small" startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>
						Refresh
					</Button>
				}>
					{mappingError}
				</Alert>
			)}

			{!mappingLoading && !mappingError && <Grid container spacing={2}>
				{mainFacilities.map((mainCode) => {
					const subFacilities = codeToFacilities[mainCode] || [];
					const selected = getSelectedForMainFacility(mainCode);
					const allSelected = selected.length === subFacilities.length;

					return (
						<Grid item xs={12} sm={6} md={4} key={mainCode}>
							<Paper
								variant="outlined"
								sx={{
									p: 2,
									height: "100%",
									borderRadius: 2,
									border: "1px solid",
									borderColor: "divider",
									transition: "border-color 0.2s, box-shadow 0.2s",
									"&:hover": {
										borderColor: "primary.light",
										boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
									},
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<WarehouseIcon sx={{ color: "primary.main", fontSize: 20 }} />
										<Typography variant="subtitle1" fontWeight={600}>
											{mainCode}
										</Typography>
									</Box>
									<Box sx={{ display: "flex", gap: 0.5 }}>
										<Button size="small" onClick={() => handleSelectAll(mainCode)} disabled={allSelected}>
											All
										</Button>
										<Button size="small" onClick={() => handleClearAll(mainCode)} disabled={selected.length === 0}>
											Clear
										</Button>
									</Box>
								</Box>
								<FormControl fullWidth size="small" variant="outlined" sx={{ mt: 0.5 }}>
									<InputLabel id={`facility-${mainCode}-label`}>
										{selected.length} of {subFacilities.length} selected
									</InputLabel>
									<Select
										labelId={`facility-${mainCode}-label`}
										id={`facility-${mainCode}-select`}
										multiple
										value={selected}
										onChange={(e) => handleMainFacilityChange(mainCode, e.target.value)}
										label={`${selected.length} of ${subFacilities.length} selected`}
										renderValue={(sel) => (
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{sel.length === subFacilities.length ? (
													<Chip label="All selected" size="small" color="primary" />
												) : sel.length === 0 ? (
													<Chip label="None selected" size="small" variant="outlined" />
												) : (
													<>
														<Chip label={`${sel.length} selected`} size="small" color="primary" variant="outlined" />
														{sel.slice(0, 2).map((value) => (
															<Chip key={value} label={value} size="small" />
														))}
														{sel.length > 2 && (
															<Chip label={`+${sel.length - 2} more`} size="small" variant="outlined" />
														)}
													</>
												)}
											</Box>
										)}
										MenuProps={{
											PaperProps: {
												sx: { maxHeight: 320, minWidth: 220, mt: 1.5, borderRadius: 2, boxShadow: 3 },
											},
											MenuListProps: { dense: true },
										}}
									>
										{subFacilities.map((subFacility) => {
											const isSelected = selected.indexOf(subFacility) > -1;
											return (
												<MenuItem key={subFacility} value={subFacility}>
													<Checkbox checked={isSelected} size="small" sx={{ mr: 1 }} />
													<ListItemText primary={subFacility} primaryTypographyProps={{ variant: "body2" }} />
												</MenuItem>
											);
										})}
									</Select>
								</FormControl>
							</Paper>
						</Grid>
					);
				})}
			</Grid>}

			<Box display="flex" justifyContent="space-between" mt={4}>
				<Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined" size="medium">
					Back
				</Button>
				<Button
					endIcon={nextLoading ? null : <ArrowForwardIcon />}
					onClick={onNext}
					variant="contained"
					size="medium"
					disabled={selectedFacilities.length === 0 || nextLoading}
					startIcon={nextLoading ? <CircularProgress size={18} color="inherit" /> : null}
				>
					{nextLoading ? "Saving…" : "Continue to Run Allocation"}
				</Button>
			</Box>
		</Box>
	);
}

export default FacilitySelection;
